import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import _ from "lodash";

import { URLS } from "config/urls";
import { AddIcon, CheckCircleIcon, DeleteOutlineOutlinedIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { PDF_MIME_TYPE } from "utils/constants";
import { BOX_MAX_WIDTH } from "utils/constants";
import { handleInvalidRequest } from "utils/sdk";
import { reverse } from "utils/urls";

import Box from "components/Box";
import Button from "components/Button";
import IconButton from "components/IconButton";
import Link from "components/Link";
import { LinearProgress } from "components/Loading";
import PageHeader from "components/PageHeader";
import Paper from "components/Paper";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";
import UploadButton from "components/UploadButton";
import { useConfirmationDialog } from "components/useConfirmationDialog";
import { usePageTitle } from "components/usePageTitle";

import {
  openAIDocumentAssistantCreate,
  openAIDocumentAssistantDelete,
  useOpenAIDocumentAssistantList,
} from "entities/AISummaries/sdk";
import { upload } from "entities/File/sdk";

const AISummariesList = () => {
  usePageTitle("Builders Patch: AI Summaries");
  const { show: showConfirmationDialog } = useConfirmationDialog();

  const params = useParams<{
    dealId: string;
  }>();
  const dealId = Number(params.dealId);

  const {
    data: documentAssistants,
    mutate: mutateDocumentAssistants,
    isValidating,
  } = useOpenAIDocumentAssistantList({ dealId });

  const handleUploadFile = useCallback(
    ({ files }: { files: Array<File> }) => {
      const file = files[0];

      if (file.type !== PDF_MIME_TYPE) {
        toast.errorMessage("Please upload a PDF file!");
        return;
      }

      const toastId = toast.loading("Uploading file...");

      upload({ file, isPrivate: false })
        .then((fileId) => {
          openAIDocumentAssistantCreate({ fileId, dealId })
            .then(() => {
              mutateDocumentAssistants();
              toast.update(toastId, {
                render: "The file successfully uploaded!",
                type: "success",
                position: "bottom-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                isLoading: false,
                closeButton: true,
              });
            })
            .catch((error) => {
              toast.update(toastId, {
                autoClose: 1,
                hideProgressBar: true,
                isLoading: false,
              });
              handleInvalidRequest(error);
            });
        })
        .catch(handleInvalidRequest);
    },
    [dealId, mutateDocumentAssistants]
  );

  const handleAssistantDelete = useCallback(
    ({ assistantId }: { assistantId: number }) => {
      showConfirmationDialog({
        onConfirm: () =>
          openAIDocumentAssistantDelete({ assistantId })
            .then(() => {
              mutateDocumentAssistants();
              toast.successMessage("File has been deleted successfully!");
            })
            .catch(handleInvalidRequest),
        confirmButtonText: "Delete",
        message: "Are you sure you want to delete this file?",
      });
    },
    [mutateDocumentAssistants, showConfirmationDialog]
  );

  const areSummariesLoading = useMemo(
    () => _.isUndefined(documentAssistants),
    [documentAssistants]
  );

  return (
    <Paper
      sx={{
        padding: theme.spacing(3, 4),
      }}
      data-testid="ai-summaries-content"
    >
      <Stack>
        <Box
          sx={{ maxWidth: BOX_MAX_WIDTH, alignSelf: "center", width: "100%" }}
        >
          <Stack spacing={3}>
            <PageHeader
              title="AI Summaries"
              actions={
                <Box>
                  <UploadButton
                    maxFiles={1}
                    accept={PDF_MIME_TYPE}
                    onChange={handleUploadFile}
                    renderComponent={() => (
                      <Button startIcon={<AddIcon />}>
                        Upload new document
                      </Button>
                    )}
                  />
                </Box>
              }
            />

            <Text
              variant="text2"
              color={colors.gray60}
              data-testid="documents-count"
            >
              {areSummariesLoading ? (
                <Skeleton width={20} sx={{ display: "inline-block" }} />
              ) : (
                documentAssistants?.length
              )}{" "}
              documents processed
            </Text>

            <Stack spacing={1} data-testid="documents-list">
              {_.isUndefined(documentAssistants) ? (
                _.range(4).map((index) => (
                  <Stack
                    key={index}
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                  >
                    <Stack direction="row" spacing={2}>
                      <Skeleton variant="text" width={30} height={30} />
                      <Skeleton variant="text" width={400} height={30} />
                    </Stack>
                    <Stack direction="row" spacing={2}>
                      <Skeleton variant="text" width={30} height={30} />
                      <Skeleton variant="text" width={30} height={30} />
                    </Stack>
                  </Stack>
                ))
              ) : (
                <>
                  {_.isEmpty(documentAssistants) ? (
                    <Text variant="h6" sx={{ marginBottom: theme.spacing(4) }}>
                      No uploaded files yet.
                    </Text>
                  ) : (
                    <>
                      {isValidating && <LinearProgress />}
                      {documentAssistants.map((assistant) => (
                        <Stack
                          key={assistant.id}
                          direction="row"
                          spacing={2}
                          justifyContent="space-between"
                          alignItems="center"
                          data-testid="document-entity"
                        >
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                          >
                            <Tooltip title="Completed">
                              <CheckCircleIcon
                                sx={{ color: colors.green100 }}
                              />
                            </Tooltip>
                            <Tooltip title={assistant.file.original_file_name}>
                              <Text
                                noWrap
                                variant="text2"
                                fontWeight={500}
                                data-testid="prompts-count"
                              >
                                {_.truncate(assistant.file.original_file_name, {
                                  length: 60,
                                })}
                              </Text>
                            </Tooltip>

                            <Text variant="text3" color={colors.gray60}>
                              {assistant.prompt_response_count} prompt(s)
                              answered
                            </Text>
                          </Stack>
                          <Stack direction="row" spacing={1}>
                            <Link
                              to={reverse(URLS.DEAL_AI_SUMMARIES_EDITOR, {
                                dealId: assistant.deal_id,
                                openAIDocumentAssistantId: assistant.id,
                              })}
                            >
                              <Button
                                size="small"
                                color="secondary"
                                data-testid="view-summary-button"
                              >
                                View summary
                              </Button>
                            </Link>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleAssistantDelete({
                                  assistantId: assistant.id,
                                })
                              }
                              data-testid="delete-document-button"
                            >
                              <DeleteOutlineOutlinedIcon
                                sx={{ width: "20px", height: "20px" }}
                              />
                            </IconButton>
                          </Stack>
                        </Stack>
                      ))}
                    </>
                  )}
                </>
              )}
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
};

export default AISummariesList;
